/**
 * components/hazard/HazardFormComposer.js — Аюул мэдээлэх / зөрчил бүртгэх /
 * шалгалт хийх маягтыг 5 хувилбараар угсарч ашиглах composer.
 *
 * Жишээ:
 *   <HazardFormComposer
 *     mode="oea"                          // naryad|oea|violation|inspection_34|inspection_full
 *     value={formState}
 *     onChange={setFormState}
 *     onSave={handleSave}
 *     onArchive={handleArchive}           // тохирох горимд л харагдана
 *     categories={categories}
 *     severities={severities}
 *     onPickFile={uploadPhoto}
 *   />
 *
 * `value` бүтэц:
 *   { photos, deptCode, deptParent, sectionCode, sectionName,
 *     locationText, gps, categoryId, hazardId, description, voiceFileUrl,
 *     severity, affectedLocation, peopleCount, selfInDanger, date,
 *     inspectionLevel, inspectionType }
 */
import React from "react";
import { getModeConfig } from "./hazardModes";
import PhotoSection from "./PhotoSection";
import LocationSection from "./LocationSection";
import HazardTypeSection from "./HazardTypeSection";
import DescriptionSection from "./DescriptionSection";
import SeveritySection from "./SeveritySection";
import WhoExposedSection from "./WhoExposedSection";
import DateSection from "./DateSection";
// Зөрчлийг арилгах арга хэмжээ · хариуцагч · хугацаа — хуучин
// зөрчлийн бүртгэлд байсан ч шинэ маягтад дутуу байсан талбарууд
import RemedySection from "./RemedySection";
// Зөрчлийн төрөл · зөрчсөн заалт · шийтгэл — лавлахаас сонгоно
import ViolationClassSection from "./ViolationClassSection";
// Мэдээллийн ангилал · эрсдэлийн түвшин · эрсдэлтэй эсэх — тайлбарын ДЭЭР
import InfoClassSection from "./InfoClassSection";
// Б/Д-аар ажилтан хайж, зураг/нэр/албан тушаал/цех/утсыг ил гаргана
import WorkerLookupSection from "./WorkerLookupSection";
import InspectionLevelSection from "./InspectionLevelSection";
import { inspectionFlowFor } from "./inspectionFlow";
import InspectionTypeSection from "./InspectionTypeSection";
import SaveBarSection from "./SaveBarSection";

/**
 * Зөрчлийн маягтын горимууд — лавлахын холбоос, зарим хэсэг эдгээрт л гарна.
 * (Аюул мэдээлэх: naryad · oea · stop_work · workplace_assessment)
 */
const VIOLATION_FORM_MODES = [
  "violation",
  "bn_violation",
  "hab_dept_violation",
  "all_violation",
];

export default function HazardFormComposer({
  mode = "naryad",
  value = {},
  onChange,
  // Lookup
  categories = [],
  hazards,
  severities,
  // «Аюулын нэр / нэрс» сонгогч — 5А/15 асуумжийн урсгалд хэрэгтэй,
  // аюул/зөрчил бүртгэх хуудсанд илүүдэл тул тэндээс false дамжуулна.
  showHazardPicker = true,
  // Photo
  onPickFile,
  uploading = false,
  // Үйлдлүүд
  onSave,
  onArchive,
  onDraft,
  onCancel,
  saving = false,
  archiving = false,
  // Хувийн тохиргоог давхар орхих боломжтой
  primaryLabelOverride,
  // Хаалттай ҮЗЛЭГИЙН ШАТ-ыг гаднаас албадах (SSO-гийн эрхээр тооцсон).
  // `null`/`undefined` бол горимын тохиргоо хэвээр үйлчилнэ.
  disabledLevelsOverride = null,
  /**
   * Тухайн ХУУДСАНД харуулахгүй хэсгүүд (ж: ["inspectionType"]).
   *
   * ⚠ ЯАГААД ГОРИМООС НЬ ХАСАХГҮЙ ВЭ:
   *   `hazardModes.js` дэх нэг горимыг ХОЁР хуудас хуваалцдаг
   *   (`/incident/register` ба `/inspection/register`). Горимоос хасвал
   *   хоёулаа алдана. Хуудас өөрөө «надад энэ хэсэг хэрэггүй» гэж
   *   хэлэх нь цорын ганц аюулгүй арга.
   */
  hideSections = null,
  /** «Зөрчлийн ангилал» картын дээд талд нэмэх агуулга (заавал биш) */
  violationClassExtra = null,
  /** «Зөрчлийн ангилал» картаас нуух талбарууд (заавал биш) */
  violationClassHideKinds = null,
  /**
   * Хэсгүүдийг ХОЁР БАГАНАД байрлуулах (заавал биш).
   *
   * ⚠ Зөвхөн ӨРГӨН цонхонд (≥900px) хоёр багана; доош нэг багана болно.
   *   Модал дотор урт маягт нь дэлгэц дүүрэн болж, хадгалах товч хүртэл
   *   гүйлгэх шаардлагатай байсныг ингэж хураана.
   */
  twoColumn = false,
  /* ТАЙЛБАРЫН ХАЖУУГИЙН НҮД — хоёр баганын горимд «Илэрсэн зөрчлийн
     дэлгэрэнгүй»-ийн баруун талд суух агуулга (ж: зөрчлийн зураг).
     ⚠ Яагаад тусдаа prop вэ: зарим маягтад зураг нь composer-ийн
       `photo` хэсгээр биш, ӨӨР хадгалалттай компонентоор (FindingPhotos
       → xab.inspection_file) ажилладаг. Тэрийг энд дамжуулж байрлуулна,
       хадгалалтын урсгалыг хөндөхгүй. */
  descriptionSide = null,
  /**
   * «Зөрчлийн ангилал» МӨРИЙН эхний баганад суух групп (зөрчлийн зураг).
   *
   * ⚠ Хэрэглэгч (2026-11-02): «зураг баримтын хэсгийг нэг групп болгон
   *   яг энэ мөрөнд зөрчлийн ангилал болон шийтгэлийн төрөлтэй
   *   3 багантай 1 мөр харуулах».
   * ⚠ Өгсөн үед тэр мөр БҮТЭН ӨРГӨН болно — эс бөгөөс 2 баганат
   *   торны хагас нүдэнд 3 групп шахагдана (хэрэглэгч: «1 багананд
   *   1 лаа шахагдаж харагдаж байна»).
   */
  violationClassLeading = null,
  /** Ангиллын мөрийг БҮТЭН ӨРГӨН болгох (2026-11-03).
   *  ⚠ `violationClassLeading`-ээс ТУСДАА: зураг тэндээс гарсан ч
   *    мөрөнд ХОЁР групп (ангилал · шийтгэл) үлддэг тул хагас
   *    нүдэнд шахагдах ёсгүй. */
  violationClassWide = false,
  /**
   * Байршил доторх ЦЕХ / ХЭСЭГ сонголтыг албадан нуух (заавал биш).
   *
   * ⚠ ЯАГААД ГОРИМООС НЬ ХАСАХГҮЙ ВЭ — `hideSections`-тэй ижил шалтгаан:
   *   `violation` горимыг `/incident/register` ба `/inspection/register`
   *   ХОЁУЛАА хуваалцдаг. `cfg.hideDeptSelectors`-ыг өөрчилвөл хоёулаа
   *   өөрчлөгдөнө.
   *
   * ⚠ Нуусан ч утга нь АЛДАГДАХГҮЙ: DepartmentTseh/Negj нь хэрэглэгчийн
   *   өөрийн код руу автоматаар тавьдаг ба зөрчил хаана гарсныг
   *   «Хариуцагч БН» (RemedySection) нарийн заана.
   */
  hideDeptSelectorsOverride = null,
  /**
   * «Зөрчлийг арилгах арга хэмжээ» хэсгийн ШИНЭ талбарууд гарах эсэх
   * (лавлахын арга хэмжээ · хариуцагч БН · эхлэл-төгсгөл хугацаа).
   *
   * ⚠ `/incident/register` ба `/inspection/register` НЭГ компонент,
   *   ИЖИЛ горим хуваалцдаг тул `hazardModes.js`-ээр ялгах боломжгүй.
   *   Хяналт шалгалтын хуудас нь өөрийн шалгах хуудсаар ажилладаг ба
   *   урьдын 4 талбартайгаа үлдэнэ.
   */
  remedyExtras = true,
  /* Гүйцэтгэх хугацааны ТӨРЛИЙН радио (2026-11-01).
     ⚠ АНХДАГЧААР ХААЛТТАЙ: төрөл нь `inspection_item_result
       .due_kind`-д хадгалагддаг тул АЖИЛЛДАГ цорын ганц
       газар нь шалгах хуудасны зөрчлийн цонх. Зөрчлийн БҮРТГЭЛ
       (/incident/register) нь `hazard_reports.sla_deadline`-д бичдэг
       өөр зам — тэнд гаргавал сонголт ЧИМЭЭГҮЙ алдагдана. */
  remedyDueKind = false,
  /* «Мэдээллийн ангилал» + «Илэрсэн зөрчлийн дэлгэрэнгүй»-г
     НЭГ МӨРӨНД, хажуу хажууд (хэрэглэгч 2026-11-01).
     ⚠ АНХДАГЧААР ХААЛТТАЙ: `violation` горимыг /incident/register
       ба шалгах хуудасны цонх ХОЁУЛАА хуваалцдаг тул бүгдэд
       асаавал хүсээгүй хуудас ч өөрчлөгдөнө.
     ⚠ Тайлбар нь ЗУРГТАЙ хосоороо байсан — энэ горимд зураг
       нь ӨӨРИЙН нүдэнд доор сууна (алдагдахгүй). */
  pairInfoWithDescription = false,
}) {
  const cfg = getModeConfig(mode);
  const hidden = Array.isArray(hideSections) ? hideSections : [];
  const sections = (cfg.sections || []).filter((k) => !hidden.includes(k));

  // Хэлбэрийг merge хийж өөрчилнө — partial update boilerplate-ыг хорогдуулах.
  //
  // ⚠ `value`-г closure-ээс биш ref-ээс уншина. Дуу хоолой бичихэд эхлээд
  // patch({description}) дуудагдаад, ~1 секундын дараа аудио байршуулалт
  // дуусахад patch({voiceFileUrl}) дуудагддаг. Closure-ийн хуучин `value`-г
  // ашиглавал хоёр дахь patch нь эхнийхийг дарж, танигдсан текст алга болдог.
  const valueRef = React.useRef(value);
  valueRef.current = value;
  const patch = (next) => onChange?.({ ...valueRef.current, ...next });

  /* `only` — зөвхөн `remedy` хэсэгт утгатай: "measure" эсвэл
     "responsible" группийг дангаар нь зурна (2026-11-02). */
  const renderSection = (key, only = null) => {
    switch (key) {
      case "photo":
        return (
          <PhotoSection
            key="photo"
            photos={value.photos || []}
            onPhotosChange={(p) => patch({ photos: p })}
            onPickFile={onPickFile}
            uploading={uploading}
          />
        );
      case "location":
        return (
          <LocationSection
            key="location"
            hideDeptSelectors={
              hideDeptSelectorsOverride == null
                ? cfg.hideDeptSelectors
                : hideDeptSelectorsOverride
            }
            deptCode={value.deptCode}
            deptParent={value.deptParent}
            sectionCode={value.sectionCode}
            locationText={value.locationText}
            gps={value.gps || { lat: null, lng: null, accuracy: null }}
            onDeptChange={(code, parentId, name) =>
              patch({
                deptCode: code,
                deptParent: parentId,
                deptName: name,
                sectionCode: "",
                sectionName: "",
              })
            }
            onSectionChange={(code, name) =>
              patch({ sectionCode: code, sectionName: name })
            }
            onLocationTextChange={(t) => patch({ locationText: t })}
            locationDescription={value.locationDescription || ""}
            onLocationDescriptionChange={(t) => patch({ locationDescription: t })}
            onGpsChange={(g) => patch({ gps: g })}
            onLocationGuess={(t) =>
              !value.locationText && patch({ locationText: t })
            }
          />
        );
      case "hazardType":
        return (
          <HazardTypeSection
            key="hazardType"
            categories={categories}
            hazards={hazards}
            value={
              value.categoryIds ||
              (value.categoryId != null ? [value.categoryId] : [])
            }
            onChange={(ids) =>
              patch({
                categoryIds: ids,
                categoryId: ids[0] ?? null, // эхнийх = үндсэн (backward-compatible)
                hazardId: null,
              })
            }
            hazardId={value.hazardId}
            onHazardIdChange={(id) => patch({ hazardId: id })}
            showHazardPicker={showHazardPicker}
          />
        );
      case "description":
        return (
          <DescriptionSection
            key="description"
            value={value.description || ""}
            onChange={(t) => patch({ description: t })}
            voiceFileUrl={value.voiceFileUrl}
            onVoiceUrl={(u) => patch({ voiceFileUrl: u })}
            title={cfg.descriptionTitle}
          />
        );
      case "severity":
        return (
          <SeveritySection
            key="severity"
            severities={severities}
            value={value.severity ?? 2}
            onChange={(lv) => patch({ severity: lv })}
          />
        );
      case "whoExposed":
        return (
          <WhoExposedSection
            key="whoExposed"
            affectedLocation={value.affectedLocation || ""}
            onAffectedChange={(t) => patch({ affectedLocation: t })}
            peopleCount={value.peopleCount || ""}
            onPeopleCountChange={(p) => patch({ peopleCount: p })}
            selfInDanger={!!value.selfInDanger}
            onSelfInDangerChange={(b) => patch({ selfInDanger: b })}
          />
        );
      case "date":
        return (
          <DateSection
            key="date"
            label={cfg.dateTitle || "Огноо"}
            value={value.date}
            onChange={(d) => patch({ date: d })}
          />
        );
      case "worker":
        return (
          <WorkerLookupSection
            key="worker"
            value={{
              worker: value.worker,
              assigneeTn: value.assigneeTn,
              assigneeName: value.assigneeName,
            }}
            onChange={patch}
          />
        );
      case "infoClass":
        return (
          <InfoClassSection
            key="infoClass"
            value={value}
            onChange={patch}
            // Энгийн ажилтны маягтад ЗӨВХӨН ангилал (hazardModes → naryad)
            // ⚠ 2026-09-20: «Хөдөлмөрийн нөхцөл» зөрчлийн бүртгэлээс хасагдсан
            //   (хэрэглэгчийн хүсэлт) — өгөгдөл/багана хэвээр, зөвхөн харуулахгүй.
            showLevel={false}
            showRisk={!cfg.infoClassOnly}
            required={!!cfg.infoClassOnly}
            /* ⚠ «Лавлах сан» холбоос нь ЗӨВХӨН зөрчлийн маягтад. Аюул
               мэдээлэх (`naryad` / `oea`) нь ЭНГИЙН АЖИЛТНЫ маягт —
               түүнийг ХАБЭА хэлтсийн лавлах удирдах хуудас руу
               хөтлөх нь буруу. */
            showRefLink={VIOLATION_FORM_MODES.includes(mode)}
          />
        );
      case "violationClass":
        return (
          <ViolationClassSection
            key="violationClass"
            value={value}
            onChange={patch}
            extraTop={violationClassExtra}
            hideKinds={violationClassHideKinds || []}
            leading={violationClassLeading}
            /* ⚠ Шийтгэлийн ДОР — «арга хэмжээ» (2026-11-02, хэрэглэгч:
               «Зөрчлийг арилгах арга хэмжээ г шийтгэлийн төрөл ийн дор
                оруулах»).
               ⚠⚠ НӨХЦӨЛ нь `violationClassWide` — өмнө нь
                 `violationClassLeading` байсан бөгөөд 2026-11-03-нд
                 зургийг тэндээс гаргахад тэр нь `null` болж, арга
                 хэмжээ ЧИМЭЭГҮЙ буцаж доошоо явсан (хэрэглэгч:
                 «яагаад гарахгүй байгаа юм бол»). Туг нь ЗУРАГТАЙ
                 биш, ХЯНАЛТ ШАЛГАЛТЫН горимтой холбоотой байх ёстой. */
            trailing={
              violationClassWide && sections.includes("remedy")
                ? renderSection("remedy", "measure")
                : null
            }
          />
        );
      case "remedy":
        return (
          <RemedySection
            only={only}
            key="remedy"
            /* ⚠ Хэрэглэдэг талбар БҮРИЙГ дурдана. Шинэ талбар нэмээд
                 ЭНД бичихээ мартвал сонголт нь үргэлж ХООСОН харагдаж,
                 хэрэглэгч «хадгалагдахгүй байна» гэж дүгнэнэ —
                 `responsibleBnCode` дээр яг ийм алдаа гарсан. */
            extras={remedyExtras}
            dueKindEnabled={remedyDueKind}
            value={{
              remedy: value.remedy,
              measure_id: value.measure_id,
              measure_text: value.measure_text,
              assigneeTn: value.assigneeTn,
              assigneeName: value.assigneeName,
              startDate: value.startDate,
              dueDate: value.dueDate,
              dueNote: value.dueNote,
              responsibleBnCode: value.responsibleBnCode,
              responsibleBnName: value.responsibleBnName,
              /* ⚠⚠ ЭНД БИЧИХЭЭ МАРТСАНААС (2026-11-02, хэрэглэгч:
                   «Хариуцагчийн төрөл болон Гүйцэтгэх хугацаа сонголт
                    хийгдэхгүй байна»).
                 `RemedySection` нь `value.dueKind` · `value.responsibleKind`
                 -ээр УНШДАГ (мөр 146 · 509). Дамжуулаагүй тул ҮРГЭЛЖ
                 `undefined` ирж, хугацааны төрөл «dated» руу, хариуцагчийн
                 төрөл «person» рүү унаж, сонголт наалддаггүй байв.
                 Талбарууд нь `rowToForm`/`formToRow`-д АЛЬ ХЭДИЙН байсан
                 тул бааз, API, шалгуур бүгд бэлэн — зөвхөн ЭНЭ гүүр
                 дутуу байв. Дээрх тайлбар яг үүнээс сэрэмжлүүлсэн. */
              dueKind: value.dueKind,
              responsibleKind: value.responsibleKind,
              /* ⚠⚠ ДАХИН ТЭР ЖЕ АЛДАА (2026-11-03): чеклэсэн
                 хариуцагчид мөн ЭНД бичигдээгүй байсан тул
                 `value.responsibleTns` үргэлж `undefined` ирж, чек
                 ОГТ тэмдэглэгддэггүй байв (хэрэглэгч: «чек хийгдэхгүй
                 байна»). Дээрх 2026-11-02-ны сануулга яг үүнээс
                 сэрэмжлүүлсэн — гэвч шинэ талбар нэмэхэд дахин
                 мартагдсан.
                 ⇒ RemedySection-д ШИНЭ талбар уншуулах бүрд ЭНЭ
                   жагсаалтад ЗААВАЛ нэм. */
              responsibleTns: value.responsibleTns,
            }}
            onChange={patch}
          />
        );
      case "inspectionLevel":
        // Шийдвэр НЭГ цэгээс — ./inspectionFlow.js. «Чухал хяналт» ба төрөл
        // сонгоогүй тохиолдолд хэсэг ерөөсөө харагдахгүй.
        if (!inspectionFlowFor(value.inspectionType, value.inspectionLevel).visible)
          return null;
        return (
          <InspectionLevelSection
            key="inspectionLevel"
            value={value.inspectionLevel ?? cfg.defaultInspectionLevel}
            onChange={(lv) => patch({ inspectionLevel: lv })}
            levels={cfg.inspectionLevels || [1, 2, 3, 4]}
            // ⚠ SSO-гоос ирсэн шатны эрх (`disabledLevelsOverride`) нь горимын
            // тогтмол тохиргооноос ДАВУУ. Ингэснээр «зөвхөн II ба IV шат»
            // гэх мэт ХОЛИМОГ эрх ч зөв харагдана — горим бүрд шинэ config
            // үүсгэх шаардлагагүй.
            disabledLevels={
              disabledLevelsOverride || cfg.inspectionLevelsDisabled || []
            }
            // Зөрчил бүртгэхдээ шат сонгосон бол тухайн шатны хяналтын
            // хуудсыг мөн бөглөх боломж (журам: шалгалт хийж зөрчил илрүүлнэ)
            showChecklist
            // Үзлэгийн ТӨРӨЛ нь удирдамжаар явагдах эсэхийг шийднэ
            // (Зорилтот / Удирдамжийн дагуу / БН-ийн удирдамжаар /
            // Төлөвлөгөөт бус). Тэр үед дугаарын шатны хуудас биш,
            // УДИРДАМЖИЙН шалгах асуудал нээгдэнэ.
            inspectionType={value.inspectionType}
            bnCode={value.deptCode}
            bnName={value.deptName}
            tsehCode={value.sectionCode}
            tsehName={value.sectionName}
          />
        );
      case "inspectionType":
        return (
          <InspectionTypeSection
            key="inspectionType"
            value={value.inspectionType}
            onChange={(t) => patch({ inspectionType: t })}
            // Объектын ангилал — журам 2.7.5 (зорилтот хяналтад л гарна)
            objectId={value.inspectionObjectId}
            onObjectChange={(id) => patch({ inspectionObjectId: id })}
            // Чухал хяналт — 15 критик хяналтын шалгах хуудас
            criticalSheet={value.criticalSheet}
            onCriticalSheetChange={(res) => patch({ criticalSheet: res })}
            criticalContext={{
              deptName:
                [value.deptName, value.sectionName].filter(Boolean).join(" · ") ||
                undefined,
              jobName: value.locationText || undefined,
            }}
          />
        );
      case "saveBar":
        return (
          <SaveBarSection
            key="saveBar"
            onSave={onSave}
            onArchive={cfg.showArchive ? onArchive : undefined}
            onDraft={cfg.showDraft ? onDraft : undefined}
            onCancel={onCancel}
            saving={saving}
            archiving={archiving}
            primaryLabel={primaryLabelOverride || cfg.primaryLabel}
          />
        );
      default:
        return null;
    }
  };

  if (!twoColumn) return <>{sections.map(renderSection)}</>;

  /* ── ХОЁР БАГАНА (2026-09-30, хэрэглэгч) ─────────────────────────────
     «Зөрчил бүртгэх хэсэг нь зарим дэлгэц дээр дүүрэн болж харагдаад
      байна … зарим нэг хэсгийг 2 багана болгоод, гар утаснаас харах үед
      бас эвтэйхэн байхаар»

     ⚠ CSS GRID хэрэглэнэ, MULTI-COLUMN (`columns`) БИШ. Multi-column нь
       картыг багана хооронд ХУВААДАГ бөгөөд заагт таарсан textarea/input
       нь Chrome/Safari дээр focus авахаа больдог (common.js дахь
       `breakInside: avoid` тайлбарыг үз). Grid-д карт бүр нэг нүдэнд
       бүтнээр сууна.

     ⚠ `align-items: start` — нүд бүр өөрийн өндрөөрөө. Үгүй бол бүх карт
       хамгийн өндрийнхөөр татагдаж, доор хоосон зай үүснэ.

     ⚠ ТАЙЛБАР (`description`) нь 2 БАГАНА эзэлнэ: тэр нь бичих талбар тул
       хагас өргөнд шахвал урт зөрчлийн тайлбар уншигдахгүй болно.

     ⚠ ТАЙЛБАР + ЗУРАГ ХОСООР (2026-10-01, хэрэглэгч: «Илэрсэн зөрчлийн
       дэлгэрэнгүй хэсгийн өргөнийг багасгаж хажууд нь зөрчлийн зураг
       оруулах хэсгийг оруулах»). Зураг нь урьд нь маягтын ЭХЭНД тусдаа
       нүдэнд сууж, тайлбар нь доор бүтэн өргөнөөр үргэлжилдэг байв —
       хоёулаа «юу болсныг» тайлбарладаг зүйл тул зэрэгцүүлбэл нүднээс
       нүд рүү гүйлгэх зай багасна. Хос нь бүтэн өргөн эзэлж, дотроо
       1.6fr / 1fr — тайлбар уншигдах өргөнөө хадгална.

     ⚠ 900px-ээс доош НЭГ багана — гар утсанд хоёр багана нь 160px болж
       сонгогч, огнооны талбарууд ашиглах аргагүй болно. */
  return (
    <>
      <style>{`
        .hfc-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          align-items: start;
        }
        /* Нүд нь агуулгаасаа болж хэтрэхээс сэргийлнэ (урт Select текст) */
        .hfc-grid > .hfc-cell { min-width: 0; }
        /* Картын доод зай нь grid-ийн gap-тай давхардахгүй */
        .hfc-grid .hz-card { margin-bottom: 0 !important; }
        /* Бүтэн өргөн эзлэх хэсгүүд */
        .hfc-grid > .hfc-description,
        .hfc-grid > .hfc-descphoto,
        /* Ангилал · шийтгэл · зураг гэсэн 3 групптэй мөр — БҮТЭН
           ӨРГӨН. Хагас нүдэнд багтаавал групп бүр ~дөрөвний нэг
           өргөнтэй шахагдана (2026-11-02). */
        .hfc-grid > .hfc-wide { grid-column: 1 / -1; }
        /* Тайлбар | зураг — тайлбар өргөн, зураг нарийн */
        .hfc-pair {
          display: grid;
          grid-template-columns: 1.6fr 1fr;
          gap: 12px;
          align-items: start;
        }
        .hfc-pair > div { min-width: 0; }
        /* ТЭНЦҮҮ хос — ангилал | дэлгэрэнгүй (2026-11-01) */
        .hfc-pair-even { grid-template-columns: 1fr 1fr; }
        .hfc-pair .hz-card { margin-bottom: 0 !important; }
        @media (max-width: 900px) {
          .hfc-grid { grid-template-columns: 1fr; gap: 10px; }
          .hfc-grid > .hfc-description,
          .hfc-grid > .hfc-descphoto,
          .hfc-grid > .hfc-wide { grid-column: auto; }
          /* Гар утсанд дээр нь тайлбар, доор нь зураг */
          .hfc-pair { grid-template-columns: 1fr; gap: 10px; }
        }
      `}</style>
      <div className="hfc-grid">
        {sections.map((name) => {
          /* Зураг нь тайлбартай ХОСООР доор гарна — энд алгасна.
             ⚠ Зөвхөн хоёулаа байгаа үед: аль нэг нь нуугдсан горимд
               (`hidden`) хос задарч, нөгөө нь ердийн нүдэндээ үлдэнэ. */
          /* ХАЖУУГИЙН НҮД: эхлээд ГАДНААС өгсөн `descriptionSide`,
             эс бөгөөс composer-ийн өөрийн `photo` хэсэг. Аль нь ч
             байхгүй бол хос үүсэхгүй — тайлбар бүтэн өргөнөөрөө. */
          const hasDesc = sections.includes("description");
          const sideEl =
            descriptionSide ||
            (sections.includes("photo") ? renderSection("photo") : null);
          const descEl = hasDesc ? renderSection("description") : null;
          const pairOk = !!(descEl && sideEl);
          /* ── АНГИЛАЛ | ДЭЛГЭРЭНГҮЙ НЭГ МӨРӨНД (2026-11-01) ───────
             ⚠ Зурагны хос (`pairOk`)-ААС ДАВУУ: хоёр хос нэг
               `description`-ыг зэрэг эзэмшиж чадахгүй. Тиймээс
               зураг нь доор ӨӨРИЙН нүдэнд сууна — агуулга
               алдагдахгүй, зөвхөн байрлал нь өөрчлөгдөнө. */
          const pairInfo =
            pairInfoWithDescription &&
            !!descEl &&
            sections.includes("infoClass");
          if (pairInfo && name === "infoClass") {
            return (
              <React.Fragment key="infoClass-description">
                <div className="hfc-cell hfc-descphoto">
                  <div className="hfc-pair hfc-pair-even">
                    <div>{renderSection("infoClass")}</div>
                    <div>{descEl}</div>
                  </div>
                </div>
                {sideEl ? (
                  <div className="hfc-cell hfc-descside">{sideEl}</div>
                ) : null}
              </React.Fragment>
            );
          }
          if (pairInfo && name === "description") return null;
          // Зургийг дээр хосын хажууд гаргасан бол дахин гаргахгүй
          if (pairInfo && !descriptionSide && name === "photo") return null;
          // Composer-ийн зургийг хосод авсан бол дахин бие даан гаргахгүй
          if (pairOk && !descriptionSide && name === "photo") return null;
          if (pairOk && name === "description") {
            return (
              <div key="description" className="hfc-cell hfc-descphoto">
                <div className="hfc-pair">
                  <div>{descEl}</div>
                  <div>{sideEl}</div>
                </div>
              </div>
            );
          }
          /* «Арга хэмжээ» нь ангиллын мөрөнд очсон тул энэ нүдэнд
             ЗӨВХӨН «Хариуцагч» үлдэнэ (2026-11-02). Үгүй бол арга
             хэмжээ ХОЁР газар давхардаж харагдана. */
          const el =
            name === "remedy" && violationClassWide
              ? renderSection("remedy", "responsible")
              : renderSection(name);
          if (!el) return null;
          /* БҮТЭН ӨРГӨН болох нүднүүд (2026-11-02, хэрэглэгч:
             «1 багананд 1 лаа шахагдаж харагдаж байна»):
               · ангиллын мөр — 3 групптэй үед,
               · арга хэмжээ — `RemedySection` нь ДОТРОО аль хэдийн
                 2 баганатай (`rs-2col`, 2026-11-01). Хагас нүдэнд
                 суувал тэр хоёр багана ~дөрөвний нэг өргөнтэй болж,
                 сонгогч, огноо ашиглах аргагүй шахагдана. */
          const wide =
            (name === "violationClass" &&
              (violationClassLeading || violationClassWide)) ||
            name === "remedy"
              ? " hfc-wide"
              : "";
          return (
            <div key={name} className={"hfc-cell hfc-" + name + wide}>
              {el}
            </div>
          );
        })}
      </div>
    </>
  );
}

export { getModeConfig } from "./hazardModes";
